import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ConfirmationDialog } from '@/components/ConfirmationDialog'
import { ModalCloseButton } from '@/components/ModalCloseButton'
import { translate, useTranslation } from '@/i18n'
import {
  useApplyManagerLineupSuggestion,
  useIgnoreManagerLineupSuggestion,
  useManagerLineupSuggestions,
  useManagerPlayers,
  useManagerRoundDetail,
  useManagerRoundAvailability,
  useRestoreManagerPreviousLineup,
  useSaveManagerDraft,
  useSubmitManagerLineup,
  type ManagerFormation,
  type ManagerLineupSuggestion,
  type ManagerLineupSuggestions,
  type ManagerPlayer,
} from '@/features/manager/api'

type SlotRef = {
  group: string
  index: number
}

type ToastState = {
  message: string
  tone: 'success' | 'error'
}

export function ManagerRoundPage() {
  const { t } = useTranslation()
  const { fantasyLeagueId, appRoundId } = useParams<{
    fantasyLeagueId: string
    appRoundId: string
  }>()
  const leagueId = Number(fantasyLeagueId)
  const roundId = Number(appRoundId)
  const validLeagueId = Number.isFinite(leagueId) ? leagueId : undefined
  const validRoundId = Number.isFinite(roundId) ? roundId : undefined
  const roundDetailQuery = useManagerRoundDetail(validLeagueId, validRoundId)
  const availabilityQuery = useManagerRoundAvailability(validLeagueId, validRoundId)
  const draftMutation = useSaveManagerDraft()
  const submitMutation = useSubmitManagerLineup()
  const restoreMutation = useRestoreManagerPreviousLineup()
  const suggestionsQuery = useManagerLineupSuggestions(validLeagueId, validRoundId, Boolean(availabilityQuery.data))
  const applySuggestionMutation = useApplyManagerLineupSuggestion()
  const ignoreSuggestionMutation = useIgnoreManagerLineupSuggestion()

  const [formationKey, setFormationKey] = useState<string>()
  const [selectedPlayers, setSelectedPlayers] = useState<Record<string, ManagerPlayer>>({})
  const [activeSlot, setActiveSlot] = useState<SlotRef>()
  const [isPickerOpen, setIsPickerOpen] = useState(false)
  const [isFormationPickerOpen, setIsFormationPickerOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [selectedTeamId, setSelectedTeamId] = useState<number>()
  const [savedMessage, setSavedMessage] = useState<string>()
  const [toast, setToast] = useState<ToastState>()
  const [isRestoreConfirmationOpen, setIsRestoreConfirmationOpen] = useState(false)
  const [pendingFormation, setPendingFormation] = useState<ManagerFormation>()
  const [suggestionBusyKey, setSuggestionBusyKey] = useState<string | null>(null)
  const autosaveTimerRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    return () => {
      if (autosaveTimerRef.current !== undefined) window.clearTimeout(autosaveTimerRef.current)
    }
  }, [])

  useEffect(() => {
    if (!toast) return

    const timeoutId = window.setTimeout(() => setToast(undefined), 3200)
    return () => window.clearTimeout(timeoutId)
  }, [toast])

  function showToast(message: string, tone: ToastState['tone'] = 'success') {
    setToast({ message, tone })
  }

  const data = availabilityQuery.data
  const lineup = data?.lineup

  function syncSelectionFromAvailability(nextData: NonNullable<typeof data>) {
    const nextLineup = nextData.lineup
    if (!nextLineup) return

    const source = nextData.draft ?? nextData.entry
    const resolvedFormation =
      source?.formation ?? nextLineup.selected_formation ?? nextLineup.default_formation ?? nextLineup.formations[0]?.key ?? undefined
    setFormationKey(resolvedFormation)

    const players = source?.players ?? []
    setSelectedPlayers(
      Object.fromEntries(
        players
          .filter((player) => player.slot && player.player_id)
          .map((player) => [slotKey(player.slot!, player.slot_index ?? 1), player]),
      ),
    )
    const initialSlots = getSlots(nextLineup.formations.find((item) => item.key === resolvedFormation))
    const firstEmptySlot = initialSlots.find(
      (slot) => !players.some((player) => player.slot && slotKey(player.slot, player.slot_index ?? 1) === slotKey(slot.group, slot.index)),
    )
    setActiveSlot(firstEmptySlot ?? initialSlots[0])
  }

  useEffect(() => {
    if (!data || !lineup || formationKey) return
    syncSelectionFromAvailability(data)
  }, [data, formationKey, lineup])

  const formation = lineup?.formations.find(
    (item) => item.key?.toUpperCase() === formationKey?.toUpperCase(),
  )
  const slots = useMemo(() => getSlots(formation), [formation])
  const selectedCount = slots.filter((slot) => selectedPlayers[slotKey(slot.group, slot.index)]).length
  const selectedPlayerIds = useMemo(
    () => new Set(Object.values(selectedPlayers).map((player) => player.player_id).filter((id): id is number => Boolean(id))),
    [selectedPlayers],
  )
  const selectedClubCount = useMemo(
    () => new Set(
      Object.values(selectedPlayers)
        .map((player) => player.team_id)
        .filter((id): id is number => Boolean(id)),
    ).size,
    [selectedPlayers],
  )
  const requiredClubCount = lineup?.team_rules?.effective_min_distinct_clubs
    ?? lineup?.team_rules?.min_distinct_clubs
    ?? 0
  // Autosaved changes live in `draft` until the round is submitted. The backend's
  // lineup.swap counters describe the submitted entry, so include the local/draft
  // swap markers as well to keep suggestions in sync with what the user sees.
  const draftSwapsUsed = useMemo(
    () => Object.values(selectedPlayers).filter((player) => player.is_new_in_round && player.player_id).length,
    [selectedPlayers],
  )
  const backendSwapsUsed = lineup?.swaps_used ?? 0
  const draftSwapsBeyondBackend = Math.max(0, draftSwapsUsed - backendSwapsUsed)
  const effectiveSwapsRemaining = lineup?.swaps_remaining !== undefined
    ? Math.max(0, lineup.swaps_remaining - draftSwapsBeyondBackend)
    : Math.max(0, (lineup?.swaps_allowed ?? 0) - Math.max(backendSwapsUsed, draftSwapsUsed))
  const swapsUsed = Math.max(backendSwapsUsed, draftSwapsUsed)
  const lineupSize = formation?.lineup_size ?? slots.length
  const isLocked = data?.app_round.is_locked === true
  const detailCanEdit = roundDetailQuery.data?.app_round.fantasy?.editability?.can_edit
  const canEditRound = detailCanEdit ?? !isLocked
  const hasEditableChangedSlot = Object.entries(selectedPlayers).some(([key, player]) => {
    if (!player.is_new_in_round || !player.player_id) return false
    const slotLocked = lineup?.locked_slots.some(
      (locked) => slotKey(locked.slot, locked.slot_index) === key,
    )
    return !slotLocked && !lineup?.locked_player_ids.includes(player.player_id)
  })
  const canEdit = canEditRound && Boolean(
    lineup?.can_create_base_team === true ||
      lineup?.submission_required === true ||
      lineup?.can_save_draft === true ||
      (lineup?.swaps_remaining ?? 0) > 0 ||
      data?.draft != null ||
    hasEditableChangedSlot,
  )
  const hasSubmittedLineup = lineup?.is_submitted === true
  const hasAvailableRestore = swapsUsed > 0
  const suggestionError = suggestionsQuery.error ?? applySuggestionMutation.error ?? ignoreSuggestionMutation.error
  const activePlayer = activeSlot ? selectedPlayers[slotKey(activeSlot.group, activeSlot.index)] : undefined
  const playersQuery = useManagerPlayers({
    leagueId: validLeagueId,
    roundId: validRoundId,
    slot: activeSlot?.group,
    teamId: selectedTeamId,
    sort: 'popular',
    query: search,
    replacePlayerId: activePlayer?.player_id,
  })
  const previousPlayer = activePlayer?.is_new_in_round && activePlayer.original_player_id
    ? playersQuery.data?.players.find((player) => player.player_id === activePlayer.original_player_id)
    : undefined

  useEffect(() => {
    if (!isPickerOpen) return

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') setIsPickerOpen(false)
    }

    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [isPickerOpen])

  function handleFormationChange(nextFormation: ManagerFormation) {
    const nextKey = nextFormation.key ?? ''
    setFormationKey(nextKey)
    setActiveSlot(undefined)
    setIsPickerOpen(false)
    setIsFormationPickerOpen(false)
    setSelectedTeamId(undefined)
    setSearch('')
    setSavedMessage(undefined)
    const allowedSlots = getSlots(nextFormation).map((slot) => slotKey(slot.group, slot.index))
    setSelectedPlayers((current) =>
      Object.fromEntries(Object.entries(current).filter(([key]) => allowedSlots.includes(key))),
    )
  }

  function requestFormationChange(nextFormation: ManagerFormation) {
    if (nextFormation.key?.toUpperCase() === formationKey?.toUpperCase()) {
      setIsFormationPickerOpen(false)
      return
    }
    setPendingFormation(nextFormation)
    setIsFormationPickerOpen(false)
  }

  function confirmFormationChange() {
    if (!pendingFormation) return
    handleFormationChange(pendingFormation)
    setPendingFormation(undefined)
  }

  function handleSelectPlayer(player: ManagerPlayer, forceRestore = false) {
    if (!activeSlot || !player.player_id) return
    const key = slotKey(activeSlot.group, activeSlot.index)
    const currentPlayerId = selectedPlayers[key]?.player_id
    const restoresPreviousPlayer = forceRestore || (
      activePlayer?.is_new_in_round === true
      && activePlayer.original_player_id === player.player_id
    )
    if (player.is_selectable === false && player.player_id !== currentPlayerId && !restoresPreviousPlayer) return
    if (player.player_id !== currentPlayerId && selectedPlayerIds.has(player.player_id)) return
    const nextPlayers = {
      ...selectedPlayers,
      [key]: {
        ...player,
        slot: activeSlot.group,
        slot_index: activeSlot.index,
        is_new_in_round: restoresPreviousPlayer
          ? false
          : currentPlayerId !== undefined
            ? true
            : player.is_new_in_round,
        original_player_id:
          restoresPreviousPlayer
            ? null
            : currentPlayerId !== undefined
            ? activePlayer?.original_player_id ?? currentPlayerId
            : player.original_player_id,
      },
    }
    setSelectedPlayers(nextPlayers)
    setSavedMessage(undefined)
    if (hasSubmittedLineup) scheduleAutosave(nextPlayers)

    const nextEmptySlot = slots.find(
      (slot) => slotKey(slot.group, slot.index) !== key && !nextPlayers[slotKey(slot.group, slot.index)],
    )
    setActiveSlot(nextEmptySlot)
    if (!nextEmptySlot) setIsPickerOpen(false)
    setSearch('')
  }

  function restorePreviousPlayer() {
    if (!previousPlayer || !canEdit) return
    handleSelectPlayer(previousPlayer, true)
    showToast(t('Wissel ongedaan gemaakt.'))
  }

  function openPlayerPicker(slot: SlotRef) {
    setActiveSlot(slot)
    setSearch('')
    setSelectedTeamId(undefined)
    setIsPickerOpen(true)
  }

  function buildPayload(players = selectedPlayers) {
    return slots
      .map((slot) => {
        const player = players[slotKey(slot.group, slot.index)]
        if (!player?.player_id) return null
        return { slot: slot.group, slot_index: slot.index, player_id: player.player_id }
      })
      .filter((player): player is { slot: string; slot_index: number; player_id: number } => player !== null)
  }

  function scheduleAutosave(players: Record<string, ManagerPlayer>) {
    if (!validLeagueId || !validRoundId || !formationKey || !canEdit || !hasSubmittedLineup) return
    if (autosaveTimerRef.current !== undefined) window.clearTimeout(autosaveTimerRef.current)

    autosaveTimerRef.current = window.setTimeout(() => {
      autosaveTimerRef.current = undefined
      draftMutation.mutate(
        {
          leagueId: validLeagueId,
          roundId: validRoundId,
          formation: formationKey,
          players: buildPayload(players),
        },
        {
          onSuccess: async () => {
            showToast(t('Wissel automatisch opgeslagen.'))
            const refreshed = await availabilityQuery.refetch()
            if (refreshed.data) syncSelectionFromAvailability(refreshed.data)
            await suggestionsQuery.refetch()
          },
          onError: (error) => {
            showToast(
              error instanceof Error ? error.message : t('Wissel automatisch opslaan is mislukt.'),
              'error',
            )
          },
        },
      )
    }, 400)
  }

  function saveDraft() {
    if (!validLeagueId || !validRoundId || !formationKey || !canEdit) return
    setSavedMessage(undefined)
    draftMutation.mutate(
      { leagueId: validLeagueId, roundId: validRoundId, formation: formationKey, players: buildPayload() },
      {
        onSuccess: () => {
          setSavedMessage('Conceptopstelling opgeslagen.')
          showToast(t('Conceptopstelling opgeslagen.'))
        },
        onError: (error) => {
          showToast(error instanceof Error ? error.message : t('Conceptopstelling opslaan is mislukt.'), 'error')
        },
      },
    )
  }

  function submitLineup() {
    if (!validLeagueId || !validRoundId || !formationKey || !canEdit || selectedCount !== lineupSize) return
    setSavedMessage(undefined)
    submitMutation.mutate(
      { leagueId: validLeagueId, roundId: validRoundId, formation: formationKey, players: buildPayload() },
      {
        onSuccess: async () => {
          setSavedMessage('Opstelling ingediend.')
          showToast(t('Opstelling ingediend.'))
          const refreshed = await availabilityQuery.refetch()
          if (refreshed.data) syncSelectionFromAvailability(refreshed.data)
        },
        onError: (error) => {
          showToast(error instanceof Error ? error.message : t('Opstelling indienen is mislukt.'), 'error')
        },
      },
    )
  }

  function applySuggestion(suggestion: ManagerLineupSuggestion) {
    const outgoingPlayerId = suggestion.outgoing.player_id
    const incomingPlayerId = suggestion.incoming.player_id
    if (!validLeagueId || !validRoundId || !outgoingPlayerId || !incomingPlayerId) return

    const busyKey = getSuggestionKey(suggestion)
    setSuggestionBusyKey(busyKey)

    applySuggestionMutation.mutate(
      {
        leagueId: validLeagueId,
        roundId: validRoundId,
        outgoingPlayerId,
        incomingPlayerId,
      },
      {
        onSuccess: async () => {
          const key = slotKey(suggestion.slot, suggestion.slot_index)
          setSelectedPlayers((current) => ({
            ...current,
            [key]: {
              ...suggestion.incoming,
              slot: suggestion.slot,
              slot_index: suggestion.slot_index,
            },
          }))
          setSavedMessage(undefined)
          showToast(t('Wissel toegepast.'))
          const refreshed = await availabilityQuery.refetch()
          if (refreshed.data) syncSelectionFromAvailability(refreshed.data)
          await suggestionsQuery.refetch()
        },
        onError: (error) => {
          showToast(error instanceof Error ? error.message : t('Wissel toepassen is mislukt.'), 'error')
        },
        onSettled: () => setSuggestionBusyKey(null),
      },
    )
  }

  function ignoreSuggestion(suggestion: ManagerLineupSuggestion) {
    const outgoingPlayerId = suggestion.outgoing.player_id
    const incomingPlayerId = suggestion.incoming.player_id
    if (!validLeagueId || !validRoundId || !outgoingPlayerId || !incomingPlayerId) return

    const busyKey = getSuggestionKey(suggestion)
    setSuggestionBusyKey(busyKey)

    ignoreSuggestionMutation.mutate(
      {
        leagueId: validLeagueId,
        roundId: validRoundId,
        outgoingPlayerId,
        incomingPlayerId,
      },
      {
        onSuccess: async () => {
          setSavedMessage('Wisselsuggestie genegeerd.')
          showToast(t('Wisselsuggestie genegeerd.'))
          await suggestionsQuery.refetch()
        },
        onError: (error) => {
          showToast(error instanceof Error ? error.message : t('Wisselsuggestie negeren is mislukt.'), 'error')
        },
        onSettled: () => setSuggestionBusyKey(null),
      },
    )
  }

  function restorePreviousLineup() {
    if (!validLeagueId || !validRoundId || !canEdit || restoreMutation.isPending) return
    setIsRestoreConfirmationOpen(true)
  }

  function confirmRestorePreviousLineup() {
    if (!validLeagueId || !validRoundId || !canEdit || restoreMutation.isPending) return
    setIsRestoreConfirmationOpen(false)

    restoreMutation.mutate(
      { leagueId: validLeagueId, roundId: validRoundId },
      {
        onSuccess: async () => {
          const refreshed = await availabilityQuery.refetch()
          if (refreshed.data) syncSelectionFromAvailability(refreshed.data)
          setSavedMessage(undefined)
          showToast(t('Vorige opstelling hersteld.'))
          await suggestionsQuery.refetch()
        },
        onError: (error) => {
          showToast(error instanceof Error ? error.message : t('Vorige opstelling herstellen is mislukt.'), 'error')
        },
      },
    )
  }

  const mutationError = draftMutation.error ?? submitMutation.error ?? restoreMutation.error

  return (
    <div className="space-y-6">
      <ConfirmationDialog
        open={isRestoreConfirmationOpen}
        title={t('Vorige opstelling herstellen?')}
        description={t('Je huidige wissels worden teruggedraaid en je vorige geldige opstelling wordt teruggezet.')}
        cancelLabel={t('Annuleren')}
        confirmLabel={t('Herstellen')}
        closeLabel={t('Sluiten')}
        confirmPending={restoreMutation.isPending}
        onCancel={() => setIsRestoreConfirmationOpen(false)}
        onConfirm={confirmRestorePreviousLineup}
      />
      <ConfirmationDialog
        open={Boolean(pendingFormation)}
        title={t('Andere formatie?')}
        description={t('Je opstelling wordt aangepast. Spelers die niet in de nieuwe formatie passen, worden verwijderd.')}
        cancelLabel={t('Annuleren')}
        confirmLabel={t('Wijzig formatie')}
        closeLabel={t('Sluiten')}
        onCancel={() => setPendingFormation(undefined)}
        onConfirm={confirmFormationChange}
      />
      {toast ? (
        <div
          role="status"
          aria-live="polite"
          className="pointer-events-none fixed inset-x-0 bottom-5 z-[60] flex justify-center px-4 sm:bottom-7"
        >
          <div className={[
            'flex items-center gap-2 rounded-full border bg-[#0d1113] px-4 py-2.5 text-sm font-bold shadow-[0_16px_40px_rgba(0,0,0,0.38)]',
            toast.tone === 'error'
              ? 'border-red-300/25 text-red-100'
              : 'border-emerald-300/20 text-emerald-100',
          ].join(' ')}>
            <span className={[
              'flex h-5 w-5 items-center justify-center rounded-full text-xs',
              toast.tone === 'error' ? 'bg-red-300/15 text-red-300' : 'bg-emerald-300/15 text-emerald-300',
            ].join(' ')} aria-hidden="true">
              {toast.tone === 'error' ? '×' : '✓'}
            </span>
            {toast.message}
          </div>
        </div>
      ) : null}

      <Link
        to={`/dashboard/manager/competities/${fantasyLeagueId}`}
        className="inline-flex items-center gap-2 text-sm font-semibold text-surface-500 transition hover:text-surface-200"
      >
        <span aria-hidden="true">←</span>
        {t('Terug naar competitie')}
      </Link>

      {availabilityQuery.isLoading ? <RoundSkeleton /> : null}

      {availabilityQuery.isError ? (
        <StateCard
          title={t('Opstelling niet geladen')}
          body={
            availabilityQuery.error instanceof Error
              ? availabilityQuery.error.message
              : t('De spelers en formaties konden niet worden opgehaald.')
          }
          action={t('Opnieuw proberen')}
          onAction={() => void availabilityQuery.refetch()}
        />
      ) : null}

      {data && lineup && formation ? (
        <>
          <header>
            <h1 className="text-3xl font-black tracking-tight text-white sm:text-4xl">
              {data.app_round.name || t('Speelronde')}
            </h1>
            <h2 className="mt-2 text-xl font-black tracking-tight text-white">{t('Bepaal je opstelling')}</h2>
          </header>

          <section className="rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-5 sm:p-6">
              <div className="flex min-w-0 items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-surface-500">{t('Basiself')}</p>
                  <button
                    type="button"
                    disabled={!canEdit}
                    onClick={() => setIsFormationPickerOpen((current) => !current)}
                    title={t('Wijzig formatie')}
                    className="group inline-flex max-w-full items-center gap-1.5 text-left text-sm font-bold text-white transition hover:text-accent-100 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <span className="truncate">{t('{formation} opstelling', { formation: formation.label || formation.key || '' })}</span>
                    <svg className="h-3.5 w-3.5 shrink-0 text-surface-500 transition group-hover:text-accent-200" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                      <path d="m10.9 2.1 3 3M9.8 3.2 3 10l-.8 3.8L6 13l6.8-6.8M2.8 13.2l3-.7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className="rounded-full border border-white/[0.08] bg-[#111719] px-2.5 py-1.5 text-xs font-bold text-surface-300">
                    {selectedCount}/{lineupSize}
                  </span>
                </div>
              </div>
              {isFormationPickerOpen ? (
                <div className="mt-3 flex flex-wrap gap-2 border-t border-white/[0.07] pt-3">
                  {lineup.formations.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      disabled={!canEdit}
                      onClick={() => requestFormationChange(item)}
                      className={[
                        'rounded-xl border px-3 py-2 text-xs font-bold transition',
                        item.key?.toUpperCase() === formationKey?.toUpperCase()
                          ? 'border-accent-300/30 bg-accent-300/15 text-accent-100'
                          : 'border-white/[0.08] bg-[#111719] text-surface-400 hover:border-white/[0.16] hover:text-white',
                        !canEdit ? 'cursor-not-allowed opacity-50' : '',
                      ].join(' ')}
                    >
                      {item.label || item.code || item.key}
                    </button>
                  ))}
                </div>
              ) : null}

              <ManagerLineupSuggestionsCard
                response={suggestionsQuery.data}
                isLoading={suggestionsQuery.isLoading}
                remainingSwaps={effectiveSwapsRemaining}
                canEdit={canEdit}
                busyKey={suggestionBusyKey}
                onApply={applySuggestion}
                onIgnore={ignoreSuggestion}
              />

              <FormationPitch
                slots={slots}
                selectedPlayers={selectedPlayers}
                activeSlot={activeSlot}
                canEdit={canEdit}
                lockedSlots={lineup.locked_slots}
                lockedPlayerIds={lineup.locked_player_ids}
                onSelect={openPlayerPicker}
              />
            </section>

          {isPickerOpen && activeSlot ? (
            <PlayerPicker
              activeSlot={activeSlot}
              activePlayer={activePlayer}
              previousPlayer={previousPlayer}
              selectedClubCount={selectedClubCount}
              requiredClubCount={requiredClubCount}
              canEdit={canEdit}
              search={search}
              onSearch={setSearch}
              onClose={() => setIsPickerOpen(false)}
              query={playersQuery}
              selectedPlayerIds={selectedPlayerIds}
              selectedTeamId={selectedTeamId}
              onTeamChange={setSelectedTeamId}
              onSelect={handleSelectPlayer}
              onRestorePreviousPlayer={restorePreviousPlayer}
            />
          ) : null}

          {mutationError ? (
            <p className="rounded-xl border border-red-400/15 bg-red-400/[0.06] px-4 py-3 text-sm text-red-200">
              {mutationError instanceof Error ? mutationError.message : t('Opslaan is niet gelukt.')}
            </p>
          ) : null}
          {suggestionError ? (
            <p className="rounded-xl border border-red-400/15 bg-red-400/[0.06] px-4 py-3 text-sm text-red-200">
              {suggestionError instanceof Error ? suggestionError.message : t('De wisselsuggestie kon niet worden verwerkt.')}
            </p>
          ) : null}
          {savedMessage ? <p className="rounded-xl border border-emerald-300/15 bg-emerald-300/[0.06] px-4 py-3 text-sm text-emerald-200">{savedMessage}</p> : null}

          {!hasSubmittedLineup ? <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              disabled={!canEdit || draftMutation.isPending || submitMutation.isPending}
              onClick={saveDraft}
              className="rounded-xl border border-white/[0.1] bg-[#111719] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#171e20] disabled:cursor-not-allowed disabled:opacity-45"
            >
              {draftMutation.isPending ? t('Opslaan…') : t('Opslaan als concept')}
            </button>
            <button
              type="button"
              disabled={!canEdit || selectedCount !== lineupSize || draftMutation.isPending || submitMutation.isPending}
              onClick={submitLineup}
              className="rounded-xl bg-accent-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-accent-500 disabled:cursor-not-allowed disabled:opacity-45"
            >
              {submitMutation.isPending ? t('Indienen…') : t('Opstelling indienen')}
            </button>
          </div> : null}
          {hasSubmittedLineup && hasAvailableRestore ? (
            <div className="flex justify-end">
              <button
                type="button"
                disabled={!canEdit || restoreMutation.isPending || draftMutation.isPending}
                onClick={restorePreviousLineup}
                className="inline-flex items-center gap-2 rounded-xl border border-red-300/15 bg-red-300/[0.05] px-4 py-3 text-sm font-bold text-red-200 transition hover:border-red-300/25 hover:bg-red-300/[0.09] disabled:cursor-not-allowed disabled:opacity-45"
              >
                <span aria-hidden="true">↶</span>
                {restoreMutation.isPending ? t('Herstellen…') : t('Vorige opstelling herstellen')}
              </button>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  )
}

function ManagerLineupSuggestionsCard({
  response,
  isLoading,
  remainingSwaps,
  canEdit,
  busyKey,
  onApply,
  onIgnore,
}: {
  response: ManagerLineupSuggestions | undefined
  isLoading: boolean
  remainingSwaps: number
  canEdit: boolean
  busyKey: string | null
  onApply: (suggestion: ManagerLineupSuggestion) => void
  onIgnore: (suggestion: ManagerLineupSuggestion) => void
}) {
  const { t } = useTranslation()
  const suggestions = response?.suggestions.slice(0, Math.min(2, Math.max(0, remainingSwaps))) ?? []

  if (remainingSwaps <= 0 || (!isLoading && suggestions.length === 0)) return null

  return (
    <section className="mt-5 overflow-hidden rounded-2xl border border-accent-300/15 bg-[#111719]">
      <div className="flex items-start justify-between gap-4 px-4 py-4 sm:px-5">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-accent-200">{t('Golero Manager')}</p>
          <h3 className="mt-1 text-base font-black tracking-tight text-white sm:text-lg">{t('Voorgestelde wissels')}</h3>
        </div>
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent-300/10 text-accent-200" aria-hidden="true">
          ✦
        </span>
      </div>

      {isLoading ? (
        <div className="border-t border-white/[0.07] px-4 py-4 sm:px-5">
          <div className="h-1.5 w-full animate-pulse rounded-full bg-accent-400/70" />
          <p className="mt-3 text-xs text-surface-500">{t('Wisselsuggesties laden…')}</p>
        </div>
      ) : (
        <div className="divide-y divide-white/[0.07] border-t border-white/[0.07]">
          {suggestions.map((suggestion) => (
            <ManagerLineupSuggestionRow
              key={getSuggestionKey(suggestion)}
              suggestion={suggestion}
              recentFormRounds={response?.basis.window_rounds ?? 5}
              canEdit={canEdit}
              isBusy={busyKey === getSuggestionKey(suggestion)}
              hasBusyAction={busyKey !== null}
              onApply={onApply}
              onIgnore={onIgnore}
            />
          ))}
        </div>
      )}
    </section>
  )
}

function ManagerLineupSuggestionRow({
  suggestion,
  recentFormRounds,
  canEdit,
  isBusy,
  hasBusyAction,
  onApply,
  onIgnore,
}: {
  suggestion: ManagerLineupSuggestion
  recentFormRounds: number
  canEdit: boolean
  isBusy: boolean
  hasBusyAction: boolean
  onApply: (suggestion: ManagerLineupSuggestion) => void
  onIgnore: (suggestion: ManagerLineupSuggestion) => void
}) {
  const { t } = useTranslation()
  const incomingPoints = suggestion.incoming.recent_points ?? suggestion.incoming.points ?? 0
  const outgoingPoints = suggestion.outgoing.recent_points ?? suggestion.outgoing.points ?? 0
  const suggestionKeyValue = getSuggestionKey(suggestion)

  return (
    <div className="px-4 py-4 sm:px-5 sm:py-5">
      <div className="grid items-center gap-2 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
        <SuggestionPlayerTile player={suggestion.incoming} label="IN" points={incomingPoints} tone="incoming" />
        <span className="mx-auto flex h-8 w-8 items-center justify-center rounded-full border border-white/[0.08] bg-[#0d1113] text-lg text-surface-400" aria-label={t('Wissel')}>
          ↔
        </span>
        <SuggestionPlayerTile player={suggestion.outgoing} label="UIT" points={outgoingPoints} tone="outgoing" />
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        <span className="font-black text-emerald-300">+{suggestion.points_delta} {t('punten')}</span>
        <span className="text-surface-500">{t('Vorm over laatste {count} speelrondes', { count: recentFormRounds })}</span>
      </div>

      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
        <button
          type="button"
          disabled={!canEdit || hasBusyAction}
          onClick={() => onApply(suggestion)}
          className="rounded-xl bg-accent-600 px-3.5 py-2.5 text-xs font-bold text-white transition hover:bg-accent-500 disabled:cursor-not-allowed disabled:opacity-45"
        >
          {isBusy ? t('Toepassen…') : t('Toepassen')}
        </button>
        <button
          type="button"
          disabled={!canEdit || hasBusyAction}
          onClick={() => onIgnore(suggestion)}
          className="rounded-xl border border-white/[0.1] bg-[#0d1113] px-3.5 py-2.5 text-xs font-bold text-surface-300 transition hover:bg-[#171e20] hover:text-white disabled:cursor-not-allowed disabled:opacity-45"
        >
          {isBusy ? t('Bezig…') : t('Negeren')}
        </button>
      </div>
    </div>
  )
}

function SuggestionPlayerTile({
  player,
  label,
  points,
  tone,
}: {
  player: ManagerPlayer
  label: 'IN' | 'UIT'
  points: number
  tone: 'incoming' | 'outgoing'
}) {
  const { t } = useTranslation()
  const incoming = tone === 'incoming'

  return (
    <div className={[
      'flex min-w-0 items-center gap-3 rounded-xl border px-3 py-3',
      incoming ? 'border-emerald-300/20 bg-emerald-300/[0.07]' : 'border-red-300/15 bg-red-300/[0.06]',
    ].join(' ')}>
      <PlayerAvatar src={player.image_path} name={player.name} className="h-11 w-11" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className={['text-[10px] font-black uppercase tracking-[0.12em]', incoming ? 'text-emerald-300' : 'text-red-300'].join(' ')}>
            {label === 'IN' ? t('IN') : t('UIT')}
          </span>
          <span className="truncate text-sm font-bold text-white">{player.name || translate('Speler')}</span>
        </div>
        <p className="mt-1 truncate text-xs text-surface-500">
          {player.team_display_name || player.team_name || translate('Club onbekend')}
          {player.position_group ? ` · ${player.position_group}` : ''}
          {player.jersey_number ? ` · #${player.jersey_number}` : ''}
        </p>
      </div>
      <span className="shrink-0 text-right text-xs font-bold text-surface-300">{points} pnt</span>
    </div>
  )
}

function getSuggestionKey(suggestion: ManagerLineupSuggestion) {
  return `${suggestion.outgoing.player_id ?? 0}:${suggestion.incoming.player_id ?? 0}`
}

function FormationPitch({
  slots,
  selectedPlayers,
  activeSlot,
  canEdit,
  lockedSlots,
  lockedPlayerIds,
  onSelect,
}: {
  slots: SlotRef[]
  selectedPlayers: Record<string, ManagerPlayer>
  activeSlot: SlotRef | undefined
  canEdit: boolean
  lockedSlots: Array<{ slot: string; slot_index: number }>
  lockedPlayerIds: number[]
  onSelect: (slot: SlotRef) => void
}) {
  const { t } = useTranslation()

  return (
    <div className="manager-formation-pitch relative mt-5 min-h-[28rem] overflow-hidden rounded-[1.25rem] border border-emerald-200/10 bg-[#0b3028]">
      <div className="pointer-events-none absolute inset-0 opacity-60">
        <div className="absolute inset-x-5 top-5 bottom-5 rounded-xl border border-white/15" />
        <div className="absolute inset-x-5 top-1/2 h-px bg-white/15" />
        <div className="absolute left-1/2 top-1/2 h-24 w-24 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/15" />
        <div className="absolute left-1/2 top-5 h-20 w-36 -translate-x-1/2 rounded-b-xl border-x border-b border-white/15" />
        <div className="absolute bottom-5 left-1/2 h-20 w-36 -translate-x-1/2 rounded-t-xl border-x border-t border-white/15" />
      </div>

      <div className="relative flex min-h-[28rem] flex-col justify-between gap-4 px-4 py-6 sm:px-8 sm:py-8">
        {groupSlots(slots).map(([group, groupItems]) => (
          <div key={group} className="flex flex-wrap justify-center gap-2 sm:gap-5">
            {groupItems.map((slot) => {
              const key = slotKey(slot.group, slot.index)
              const player = selectedPlayers[key]
              const isActive = activeSlot ? slotKey(activeSlot.group, activeSlot.index) === key : false
              const isSlotLocked = lockedSlots.some(
                (locked) => slotKey(locked.slot, locked.slot_index) === key,
              ) || Boolean(player?.player_id && lockedPlayerIds.includes(player.player_id))

              return (
                <button
                  key={key}
                  type="button"
                  disabled={!canEdit || isSlotLocked}
                  onClick={() => onSelect(slot)}
                  className={[
                    'manager-player-slot relative z-10 flex w-[4.75rem] flex-col items-center gap-1 rounded-xl px-1 py-1.5 text-center transition sm:w-24',
                    isActive ? 'bg-accent-300/15 ring-2 ring-accent-300/50' : 'hover:bg-white/[0.07]',
                    !canEdit || isSlotLocked ? 'cursor-not-allowed opacity-60' : '',
                  ].join(' ')}
                >
                  <span className="relative">
                    <PlayerAvatar
                      src={player?.image_path}
                      name={player?.name || slot.group}
                      className="h-11 w-11 border-2 border-white/20 shadow-lg sm:h-14 sm:w-14"
                    />
                    {player?.is_new_in_round ? (
                      <span
                        className="absolute -left-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full border border-[#0b3028] bg-[#111820] text-accent-200"
                        title={t('Nieuwe speler deze speelronde')}
                        aria-label={t('Nieuwe speler deze speelronde')}
                      >
                        <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                          <path d="M2.5 5.5h10M10 2.5l3 3-3 3M13.5 10.5h-10M6 7.5l-3 3 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                    ) : null}
                    {isSlotLocked ? (
                      <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full border border-[#0b3028] bg-amber-300 text-[10px] text-black" aria-label={t('Vast')}>
                        🔒
                      </span>
                    ) : null}
                  </span>
                  <span className="w-full truncate text-[10px] font-bold text-white sm:text-xs">
                    {player?.name || `${slot.group} ${slot.index}`}
                  </span>
                  <span className="text-[9px] font-semibold uppercase tracking-[0.12em] text-white/50">
                    {player ? (player.team_display_name || player.team_name || slot.group) : 'Kies'}
                  </span>
                </button>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}

function groupSlots(slots: SlotRef[]) {
  const grouped = slots.reduce<Record<string, SlotRef[]>>((groups, slot) => {
    groups[slot.group] = [...(groups[slot.group] ?? []), slot]
    return groups
  }, {})

  return Object.entries(grouped).sort(([left], [right]) => {
    const leftRank = formationGroupOrder(left)
    const rightRank = formationGroupOrder(right)
    return leftRank - rightRank || left.localeCompare(right)
  })
}

function formationGroupOrder(group: string) {
  const order = ['GK', 'DEF', 'MID', 'FWD', 'ATT']
  const index = order.indexOf(group.toUpperCase())
  return index === -1 ? order.length : index
}

function PlayerPicker({
  activeSlot,
  activePlayer,
  previousPlayer,
  selectedClubCount,
  requiredClubCount,
  canEdit,
  search,
  onSearch,
  onClose,
  query,
  selectedPlayerIds,
  selectedTeamId,
  onTeamChange,
  onSelect,
  onRestorePreviousPlayer,
}: {
  activeSlot: SlotRef | undefined
  activePlayer: ManagerPlayer | undefined
  previousPlayer: ManagerPlayer | undefined
  selectedClubCount: number
  requiredClubCount: number
  canEdit: boolean
  search: string
  onSearch: (value: string) => void
  onClose: () => void
  query: ReturnType<typeof useManagerPlayers>
  selectedPlayerIds: Set<number>
  selectedTeamId: number | undefined
  onTeamChange: (teamId: number | undefined) => void
  onSelect: (player: ManagerPlayer, forceRestore?: boolean) => void
  onRestorePreviousPlayer: () => void
}) {
  const { t } = useTranslation()
  const [stableTeams, setStableTeams] = useState(query.data?.teams ?? [])
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (query.data?.teams) setStableTeams(query.data.teams)
  }, [query.data?.teams])

  const teams = stableTeams

  function handleTeamChange(teamId: number | undefined) {
    onTeamChange(teamId)
    window.requestAnimationFrame(() => {
      contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
    })
  }

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        onClick={onClose}
        aria-label={t('Sluit spelerselectie')}
        className="animate-drawer-backdrop-in absolute inset-0 bg-black/80 backdrop-blur-sm"
      />
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="manager-player-picker-title"
        className="animate-manager-player-picker-in absolute inset-x-0 bottom-0 mx-auto flex h-[92dvh] max-h-[92dvh] w-full max-w-5xl flex-col overflow-hidden rounded-t-[1.5rem] border-t border-white/[0.1] bg-[#0d1113] shadow-[0_-20px_64px_rgba(0,0,0,0.5)]"
      >
        <header className="shrink-0 border-b border-white/[0.07] p-5 sm:p-6">
          <div className="flex min-w-0 items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-surface-500">{t('Spelers bekijken')}</p>
              <h2 id="manager-player-picker-title" className="mt-1 text-xl font-black tracking-tight text-white sm:text-2xl">
                {activePlayer ? t('Vervang speler') : t('Selecteer speler')}
              </h2>
              <p className="mt-1 text-sm text-surface-500">
                {t('Positie {position}', { position: activeSlot ? `${activeSlot.group} ${activeSlot.index}` : '—' })}
              </p>
            </div>
            <ModalCloseButton onClick={onClose} label={t('Sluiten')} className="h-9 w-9" />
          </div>

          {requiredClubCount > 0 || (activePlayer?.is_new_in_round && previousPlayer) ? (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              {requiredClubCount > 0 ? (
                <span className={[
                  'inline-flex items-center rounded-full border px-3 py-1.5 text-xs font-bold',
                  selectedClubCount >= requiredClubCount
                    ? 'border-emerald-300/25 bg-emerald-300/10 text-emerald-200'
                    : 'border-amber-300/25 bg-amber-300/10 text-amber-100',
                ].join(' ')}>
                  {t('{selected}/{required} clubs geselecteerd', {
                    selected: selectedClubCount,
                    required: requiredClubCount,
                  })}
                </span>
              ) : <span />}
              {activePlayer?.is_new_in_round && previousPlayer ? (
                <InlineSwapAction
                  previousPlayer={previousPlayer}
                  activePlayer={activePlayer}
                  canEdit={canEdit}
                  onRestore={onRestorePreviousPlayer}
                />
              ) : null}
            </div>
          ) : null}

          {activePlayer && !(activePlayer.is_new_in_round && previousPlayer) ? (
              <div className="mt-5 flex items-center gap-4 rounded-2xl border border-accent-300/15 bg-accent-300/[0.06] p-4 sm:p-5">
                <PlayerAvatar src={activePlayer.image_path} name={activePlayer.name} className="h-14 w-14 text-base" />
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-accent-200">{t('Huidige speler')}</p>
                  <p className="mt-1 truncate text-base font-bold text-white">{activePlayer.name || t('Speler')}</p>
                  <p className="mt-1 truncate text-sm text-surface-400">
                    {activePlayer.team_display_name || activePlayer.team_name || t('Club onbekend')}
                    {activePlayer.position_group ? ` · ${activePlayer.position_group}` : ''}
                    {activePlayer.jersey_number ? ` · #${activePlayer.jersey_number}` : ''}
                  </p>
                </div>
              </div>
          ) : null}
        </header>

        {!activeSlot ? (
          <div className="p-6 text-center text-sm text-surface-500">
            {t('Selecteer een positie om spelers te bekijken.')}
          </div>
        ) : (
        <div ref={contentRef} className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto p-5 sm:p-6">
          <div className="grid min-w-0 gap-6 lg:grid-cols-[13rem_minmax(0,1fr)]">
            <aside className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-surface-500">{t('Clubs')}</p>
              <div className="no-scrollbar mt-3 flex w-full min-w-0 max-w-full touch-pan-x gap-2 overflow-x-auto overscroll-x-contain pb-1 lg:grid lg:grid-cols-1 lg:overflow-visible lg:pb-0">
                <button
                  type="button"
                  onClick={() => handleTeamChange(undefined)}
                  className={[
                    'shrink-0 rounded-xl border px-3 py-2.5 text-left text-xs font-bold transition lg:w-full',
                    selectedTeamId === undefined
                      ? 'border-accent-300/25 bg-accent-300/10 text-accent-100'
                      : 'border-white/[0.08] bg-[#111719] text-surface-400 hover:text-white',
                  ].join(' ')}
                >
                  {t('Alle clubs')}
                </button>
                {teams.map((team) => (
                  <button
                    key={team.id}
                    type="button"
                    onClick={() => handleTeamChange(team.id)}
                    className={[
                      'shrink-0 rounded-xl border px-3 py-2.5 text-left text-xs font-bold transition lg:w-full',
                      selectedTeamId === team.id
                        ? 'border-accent-300/25 bg-accent-300/10 text-accent-100'
                        : 'border-white/[0.08] bg-[#111719] text-surface-400 hover:text-white',
                    ].join(' ')}
                  >
                    {team.display_name || team.name || `Club ${team.id}`}
                  </button>
                ))}
              </div>
            </aside>

            <div className="min-w-0">
              <div className="relative mt-0">
            <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-surface-600">⌕</span>
            <input
              value={search}
              onChange={(event) => onSearch(event.target.value)}
              placeholder={t('Zoek speler')}
              className="w-full rounded-xl border border-white/[0.08] bg-[#111719] py-3 pl-9 pr-3 text-sm text-white outline-none transition placeholder:text-surface-600 focus:border-accent-300/30"
            />
          </div>

              <div className="mt-5 grid gap-2 sm:grid-cols-2">
            {query.isLoading ? <p className="py-8 text-center text-sm text-surface-500">{t('Spelers laden…')}</p> : null}
            {query.isError ? (
              <p className="py-8 text-center text-sm text-red-300">
                {query.error instanceof Error ? query.error.message : t('Spelers konden niet worden geladen.')}
              </p>
            ) : null}
            {!query.isLoading && !query.isError && query.data?.players.length === 0 ? (
              <p className="py-8 text-center text-sm text-surface-500">{t('Geen spelers gevonden.')}</p>
            ) : null}
            {query.data?.players.map((player) => (
              <PlayerOption
                key={player.player_id}
                player={player}
                isCurrent={player.player_id === activePlayer?.player_id}
                isRestoreTarget={player.player_id === previousPlayer?.player_id}
                isSelectedElsewhere={player.player_id !== activePlayer?.player_id && selectedPlayerIds.has(player.player_id ?? 0)}
                canEdit={canEdit}
                onSelect={onSelect}
              />
            ))}
              </div>
            </div>
          </div>
        </div>
      )}
      </section>
    </div>
  )
}

function PlayerOption({
  player,
  isCurrent,
  isRestoreTarget,
  isSelectedElsewhere,
  canEdit,
  onSelect,
}: {
  player: ManagerPlayer
  isCurrent: boolean
  isRestoreTarget: boolean
  isSelectedElsewhere: boolean
  canEdit: boolean
  onSelect: (player: ManagerPlayer) => void
}) {
  const { t } = useTranslation()
  const unavailable = player.is_selectable === false || player.selection_block_reasons.length > 0
  const blocked = !isCurrent && !isRestoreTarget && (unavailable || isSelectedElsewhere)
  const status = isCurrent
    ? t('Huidige speler')
    : isRestoreTarget
      ? t('Vorige speler terugzetten')
      : isSelectedElsewhere
      ? t('Al gekozen')
      : unavailable
        ? player.selection_status_message || t('Niet beschikbaar')
        : t('Vervang door deze speler')

  return (
    <button
      type="button"
      disabled={!canEdit || blocked}
      onClick={() => onSelect(player)}
      className={[
        'flex w-full items-center gap-3 rounded-xl border px-3 py-3 text-left transition',
        isCurrent
          ? 'border-accent-300/20 bg-accent-300/[0.07]'
          : blocked
            ? 'border-white/[0.06] bg-[#101516] opacity-60'
            : 'border-white/[0.07] bg-[#111719] hover:border-accent-300/25 hover:bg-[#151c1e]',
        !canEdit || blocked ? 'cursor-not-allowed' : '',
      ].join(' ')}
    >
      <PlayerAvatar src={player.image_path} name={player.name} />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="truncate text-sm font-semibold text-white">{player.name || t('Speler')}</span>
          {player.jersey_number ? <span className="shrink-0 text-[10px] font-bold text-surface-600">#{player.jersey_number}</span> : null}
        </span>
        <span className="mt-1 block truncate text-xs text-surface-500">
          {player.team_display_name || player.team_name || t('Club onbekend')}
          {player.position_group ? ` · ${player.position_group}` : ''}
        </span>
      </span>
      <span className="shrink-0 text-right">
        {player.selection_count != null ? <span className="block text-[10px] text-surface-600">{player.selection_count} gekozen</span> : null}
        <span className={['mt-1 block max-w-36 truncate text-[10px] font-bold', isCurrent ? 'text-accent-200' : blocked ? 'text-amber-300' : 'text-accent-200'].join(' ')}>
          {status}
        </span>
      </span>
    </button>
  )
}

function InlineSwapAction({
  previousPlayer,
  activePlayer,
  canEdit,
  onRestore,
}: {
  previousPlayer: ManagerPlayer
  activePlayer: ManagerPlayer
  canEdit: boolean
  onRestore: () => void
}) {
  const { t } = useTranslation()

  return (
    <div className="flex min-w-0 items-center gap-1.5 text-xs">
      <div className="flex min-w-0 items-center gap-1.5">
        <PlayerAvatar src={previousPlayer.image_path} name={previousPlayer.name} className="h-7 w-7" />
        <div className="min-w-0">
          <p className="text-[9px] font-black uppercase tracking-[0.1em] text-emerald-300">{t('IN')}</p>
          <p className="max-w-24 truncate font-bold text-white">{previousPlayer.name || t('Speler')}</p>
        </div>
      </div>
      <button
        type="button"
        disabled={!canEdit}
        onClick={onRestore}
        aria-label={t('Wissel ongedaan maken')}
        title={t('Wissel ongedaan maken')}
        className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-white/[0.1] bg-[#111719] px-2.5 py-1.5 text-[10px] font-bold text-surface-300 transition hover:border-white/[0.18] hover:bg-[#171e20] hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
      >
        <span className="text-sm leading-none" aria-hidden="true">↶</span>
        <span>{t('Wissel ongedaan maken')}</span>
      </button>
      <div className="flex min-w-0 items-center gap-1.5">
        <PlayerAvatar src={activePlayer.image_path} name={activePlayer.name} className="h-7 w-7" />
        <div className="min-w-0">
          <p className="text-[9px] font-black uppercase tracking-[0.1em] text-red-300">{t('UIT')}</p>
          <p className="max-w-24 truncate font-bold text-white">{activePlayer.name || t('Speler')}</p>
        </div>
      </div>
    </div>
  )
}

function PlayerAvatar({
  src,
  name,
  className = '',
}: {
  src: string | null | undefined
  name: string | null | undefined
  className?: string
}) {
  const imageClassName = ['h-10 w-10 shrink-0 rounded-full bg-[#172024] object-cover', className].filter(Boolean).join(' ')
  const fallbackClassName = ['flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#172024] text-sm font-bold text-accent-200', className].filter(Boolean).join(' ')

  return src ? (
    <img src={src} alt="" className={imageClassName} />
  ) : (
    <span className={fallbackClassName}>
      {(name || 'S').slice(0, 1).toUpperCase()}
    </span>
  )
}

function getSlots(formation?: ManagerFormation) {
  if (!formation?.slots) return []
  return Object.entries(formation.slots).flatMap(([group, count]) =>
    Array.from({ length: count }, (_, index) => ({ group, index: index + 1 })),
  )
}

function slotKey(group: string, index: number) {
  return `${group.toUpperCase()}#${index}`
}

function StateCard({ title, body, action, onAction }: { title: string; body: string; action: string; onAction: () => void }) {
  return (
    <section className="rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-6 sm:p-8">
      <h1 className="text-xl font-black text-white">{title}</h1>
      <p className="mt-2 text-sm leading-6 text-surface-400">{body}</p>
      <button type="button" onClick={onAction} className="mt-5 rounded-xl bg-accent-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-accent-500">
        {action}
      </button>
    </section>
  )
}

function RoundSkeleton() {
  const { t } = useTranslation()
  return (
    <div className="space-y-6" aria-label={t('Opstelling laden')}>
      <div className="h-20 animate-pulse rounded-2xl bg-[#0d1113]" />
      <div className="h-44 animate-pulse rounded-[1.5rem] bg-[#0d1113]" />
      <div className="h-96 animate-pulse rounded-[1.5rem] bg-[#0d1113]" />
    </div>
  )
}
